import { IEquals } from '@common/interfaces/i-equals';
import { DateTime } from '@common/data-models/complex-types/date-time';
import { MultiValue } from '@common/data-models/complex-types/multi-value';
import { Table } from '@common/data-models/complex-types/table';
import { LookupList } from '@common/data-models/complex-types/lookup-list';
import { Address } from '@common/data-models/complex-types/address';
import { TimeZone } from '@common/data-models/complex-types/time-zone';
import { ChecklistSerializer, SpecialSerializer, LookupListSerializer, AddressSerializer, StaffIDListSerializer, RelationshipSerializer, DateTimeRangeSerializer } from '@common/data-models/serializers/serializers';
import { Util } from "@common/utils/util";
import { IEnpsRecordControl } from '@common/interfaces/i-enps-record-control'
import { Relationship } from '@suite/common/data-models/complex-types/relationship';
import { DateRange } from '@suite/common/data-models/complex-types/date-range';
import { FieldDef } from '@common/data-models/service/field-def';
import { Record } from '@common/data-models/records/record';
import { AppService } from '@src/suite/app.service';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { BasicContentService } from '@common/services/basic-content-service';

export class EnpsRecordControl {

    static appService: AppService;
    static globalTablesService: GlobalTablesService;
    static basicContentService: BasicContentService

    public static preserveOldValue<T extends IEquals<T> | string | boolean | Array<any>>(control: IEnpsRecordControl<T>) {
        if (control.record && control.record.guid) {
            // let currentValue = (control.specialRow >= 0) ? control.record.getAs<T>(control.property) : control.value;
            let currentValue = control.value;
            // Relevant for existing records only
            if (
                (typeof currentValue == "string") ||
                (typeof currentValue == "number") ||
                (typeof currentValue == "boolean") ||
                (typeof currentValue == "undefined") ||
                (control.value instanceof TimeZone)
            ) {
                control.oldValue = currentValue;
            } else if (control.constr) {
                control.oldValue = JSON.stringify(currentValue);
            } else {
                Util.saveWarningError("EnpsControl.preserveOldValue() not implemented for type " + typeof (currentValue));
            }
        }
    }

    public static isChanged<T extends IEquals<T> | string | boolean | Array<any>>(control: IEnpsRecordControl<T>): boolean {
        /*if (!this.record.guid) {
            return true;
        } else {*/
        // let currentValue = (control.specialRow >= 0) ? control.record.getAs<T>(control.property) : control.value;
        let currentValue = control.value;
        if (
            (typeof currentValue == "string") ||
            (typeof currentValue == "number") ||
            (typeof currentValue == "boolean") ||
            (typeof currentValue == "undefined")
        ) {
            return (control.oldValue != currentValue);
        } else if (control.constr) {
            let oldValue = null;
            if (control.oldValue) {
                let oldValueObj = (currentValue["fromJSON"]) ? currentValue["fromJSON"](<string>control.oldValue) : JSON.parse(<string>control.oldValue);
                oldValue = Object.assign(new control.constr, oldValueObj);
            }
            if ((currentValue instanceof DateTime) && oldValue && oldValue.get) {
                oldValue.set(new Date(oldValue.get()));
            }
            if ((currentValue instanceof LookupList) && oldValue instanceof LookupList) {
                return !(oldValue.keys.length == currentValue.keys.length); // TODO: functionalize it
            }
            if ((currentValue instanceof Address) && oldValue instanceof Address) {
                if (currentValue.placeId != '' && (currentValue.placeId != oldValue.placeId))
                    return true;
                else {
                    if (currentValue.hasManualEdits(oldValue)) {
                        currentValue.clearGoogleFields();
                        return true;
                    }
                    return false;
                }
            }
            if ((currentValue instanceof Address) && oldValue === null) {
                return true;
            }
            return !(<IEquals<T>>currentValue).equals(oldValue);
        } else {
            Util.saveWarningError("EnpsControl.isChanged() not implemented for type " + typeof (currentValue));
        }
        //}
    }

    public static async saveChanges<T extends IEquals<T> | string | boolean | Array<any>>(currentValue: T, fieldId: string, record: Record) {
        if (!record.guid) {
            //Create
            record.set(fieldId, currentValue);
            EnpsRecordControl.appService.recordChanged(record);
        } else {
            // Update
            // let currentValue = (control.specialRow >= 0) ? control.record.getAs<T>(control.property) : control.value;
            // let currentValue = control.value;
            // if (this.isChanged<T>(control)) {
            //     try {
            let data: { [id: string]: any } = {};

            let updateValue: T | string;
            if (
                (typeof currentValue == "string") ||
                (typeof currentValue == "number") ||
                (typeof currentValue == "boolean")
            ) {
                if (fieldId == EnpsRecordControl.appService.PSTFieldNames.PSTTopicSlug && typeof currentValue == "string" && currentValue.toString().trim() == "") {
                    // this.restoreOldValue<T>(control);
                    return;
                } else {
                    updateValue = currentValue;
                }
            } else if (currentValue instanceof DateTime) {
                if (currentValue.moment) {
                    updateValue = currentValue.moment.toISOString();
                } else {
                    updateValue = null;
                }
            } else if (Array.isArray(currentValue)) {
                if (EnpsRecordControl.globalTablesService.isStaffIDListFieldType(fieldId)) {
                    updateValue = StaffIDListSerializer.Instance(EnpsRecordControl.globalTablesService).serialize(currentValue, fieldId);
                } else if (EnpsRecordControl.globalTablesService.isCheckListFieldType(fieldId)) {
                    updateValue = ChecklistSerializer.Instance.serialize(currentValue);
                } else if (EnpsRecordControl.globalTablesService.isLookupListFieldType(fieldId)) {
                    updateValue = LookupListSerializer.Instance(EnpsRecordControl.globalTablesService).serialize(currentValue as LookupList);
                }
            } else if (currentValue instanceof Address) {
                updateValue = AddressSerializer.Instance(EnpsRecordControl.globalTablesService).serialize(currentValue);
            } else if (currentValue instanceof DateRange) {
                updateValue = DateTimeRangeSerializer.Instance().serialize(currentValue);
            } else if (currentValue instanceof Relationship) {
                updateValue = RelationshipSerializer.Instance(EnpsRecordControl.globalTablesService).serialize(currentValue);
            } else if (currentValue instanceof Table) {
                updateValue = SpecialSerializer.Instance(EnpsRecordControl.globalTablesService).serialize(currentValue);
            } else {
                Util.saveWarningError("EnpsControl.saveChanges() not implemented for type " + typeof (currentValue));
                return;
            }

            data[fieldId] = updateValue;
            // if (additionalData) {
            //     for (let prop in additionalData) {
            //         data[prop] = additionalData[prop];
            //     }
            // }

            // Make sure to pass record type
            data["Type"] = record.type;

            let res = await EnpsRecordControl.basicContentService.updateBasicContent(record.database, record.path.fullPath, record.guid, false, data, false, null);
            if (!res.status) {
                // this.restoreOldValue<T>(control);
                // TODO: error handling
            } else {
                EnpsRecordControl.appService.recordChanged(record);
            }
            // } catch (e) {
            //     console.log(e);
            //     this.restoreOldValue<T>(control);
            //     // TODO: error handling
            // }
            //}
        }
    }

    public static restoreOldValue<T extends IEquals<T> | string | boolean | Array<any>>(control: IEnpsRecordControl<T>) {

        if (!control.record) {
            Util.saveWarningError("control.record is null/undefined !");
            return;
        }
        if (control.record.guid) {
            // Relevant for existing records only
            if (control.constr) {
                let oldObj = Object.assign(new control.constr, JSON.parse(<string>control.oldValue));
                // if (control.specialRow >= 0) {
                //     control.value = (<Table>oldObj).get(control.specialRow, control.specialColumn);
                // } else {
                control.value = oldObj;
                // }
            } else if (
                (typeof control.oldValue == "string") ||
                (typeof control.oldValue == "number") ||
                (typeof control.oldValue == "boolean") ||
                (control.oldValue instanceof TimeZone)
            ) {
                control.value = <T>control.oldValue;
            } else {
                Util.saveWarningError("EnpsControl.restoreOldValue() not implemented for type " + typeof (control.value));
            }
        }
    }

    public static checkPrivileges<T extends IEquals<T> | string | boolean | Array<any>>(control: IEnpsRecordControl<T>) {
        if (control.record) {
            if (control.editable) {
                control.editable = control.record.getSecurityPrivsWrite(control.appService) && !(control.globalTablesService.FieldDefsDict[control.property] && control.globalTablesService.FieldDefsDict[control.property].ReadOnlyValue);
            }
        }
    }
}



